/**
 * Auth API hooks — переключатель аккаунтов (AK-469).
 *
 * Backend contract — backend/src/ikolhoz/api/v1/auth.py + profile.py:
 *   GET    /api/v1/auth/linked-accounts  → { items: LinkedAccount[] }
 *   POST   /api/v1/auth/switch-account   → 204 { user_id, step_up_token? }
 *   POST   /api/v1/auth/step-up          → { step_up_token }
 *                                          { code, purpose, target_user_id? }
 *   DELETE /api/v1/profile/me/sessions   → 204 (выход со всех устройств)
 *
 * Привязка аккаунтов живёт в httpOnly-cookie `linked_sessions` (path
 * `/api/v1/auth`) — фронт её не видит и видеть не должен: список приходит
 * только из `/auth/linked-accounts`.
 *
 * `target_user_id` в step-up — намеренно: проверяется 2FA **целевого**
 * аккаунта, а не текущего. Founder переключается ИЗ тенантовского аккаунта
 * (2FA обычно нет) В платформенный (2FA обязательна), поэтому спрашивать код
 * текущего аккаунта бессмысленно.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Типы (mirror Pydantic) ───────────────────────────────────────────────

export interface LinkedAccount {
  user_id: string;
  full_name: string | null;
  email: string;
  /** Аккаунт, под которым работаем прямо сейчас. */
  is_current: boolean;
  /** Сессия отозвана / просрочена — переключиться нельзя, нужен вход заново. */
  is_expired: boolean;
  /** Нужен код из приложения-аутентификатора (прод + платформенный аккаунт). */
  requires_step_up: boolean;
}

export interface LinkedAccountsList {
  items: LinkedAccount[];
}

export type StepUpPurpose = 'switch_account' | 'impersonate';

export interface StepUpPayload {
  code: string;
  purpose: StepUpPurpose;
  /** Для switch_account — аккаунт, В который переключаемся (его 2FA и проверяем). */
  target_user_id?: string;
}

export interface StepUpResult {
  step_up_token: string;
}

export interface SwitchAccountPayload {
  user_id: string;
  step_up_token?: string;
}

// ─── Query keys ───────────────────────────────────────────────────────────

export const authKeys = {
  linkedAccounts: ['auth', 'linked-accounts'] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────────

/**
 * GET /auth/linked-accounts — аккаунты, привязанные к этому браузеру.
 *
 * `enabled` по умолчанию false: запрос имеет смысл только когда меню профиля
 * реально открыли. Дёргать его на каждой загрузке страницы у всех
 * пользователей — лишний round-trip ради меню, которое большинство не
 * открывает.
 */
export function useLinkedAccounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authKeys.linkedAccounts,
    queryFn: () => api.get<LinkedAccountsList>('/api/v1/auth/linked-accounts'),
    staleTime: 30_000,
    enabled: options?.enabled ?? false,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

/**
 * POST /auth/switch-account — переключение без пароля.
 *
 * После успеха чистим **весь** кэш TanStack Query: в нём лежат поля, сезоны и
 * справочники предыдущего аккаунта, а cookie уже другая. Вызывающий сразу
 * после этого делает жёсткий переход на главную — оба шага вместе гарантируют,
 * что чужие данные не мелькнут на экране.
 */
export function useSwitchAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SwitchAccountPayload) =>
      api.post<void>('/api/v1/auth/switch-account', payload),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/** POST /auth/step-up — обмен кода из приложения на короткоживущий токен (5 мин). */
export function useStepUp() {
  return useMutation({
    mutationFn: (payload: StepUpPayload) =>
      api.post<StepUpResult>('/api/v1/auth/step-up', payload),
  });
}

/**
 * DELETE /profile/me/sessions — выход со всех устройств, включая текущее.
 *
 * Рубит и привязки других аккаунтов (backend удаляет cookie `linked_sessions`),
 * поэтому кэш чистим целиком, а вызывающий уходит на /login.
 */
export function useRevokeAllSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/api/v1/profile/me/sessions'),
    onSuccess: () => {
      qc.clear();
    },
  });
}
